import {throwError as observableThrowError, Subject, range, timer } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from './../../app/services/auth.service';
import { CustomBackendLoggerService } from 'modules/app/services/custom-backend-logger.service';
import { PushXService } from './pushx.service';


import { ApiGatewayHelperService } from './../../app/services/apigateway-helper.service';
import { mergeMap, retryWhen, zip } from 'rxjs/operators';

@Injectable()
export class RecordingHelperService {

    public pushXEventSubject = new Subject<any>();

    private asLyrApiGatewayInfo;
    private pushXListenerExist = false;

    constructor(
        private http: HttpClient,
        private apiGatewayHelperService: ApiGatewayHelperService,
        private authService : AuthService,
        private pushXService: PushXService,
        private customBackendLoggerService: CustomBackendLoggerService,
    ) { }

    public async initializeForUplaod() {
        if (!this.asLyrApiGatewayInfo) {
            this.asLyrApiGatewayInfo = await this.http.get(
                this.apiGatewayHelperService.getApiGatewayPrefixUrl() + 'startAudioRecording/' + this.apiGatewayHelperService.getSpaceId()
            ).toPromise();
        }
        return this.asLyrApiGatewayInfo.orgId;
    }

    public uploadAudioPart(PartitionKey, Data) {
        return this.http.post(this.asLyrApiGatewayInfo.apiGatewayUrl, { PartitionKey, Data }, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'x-api-key': this.asLyrApiGatewayInfo.apiKey,
            })
        }).pipe(retryWhen(
            attempts => range(1, 4).pipe(
                zip(attempts, i => i),
                mergeMap(i => {
                    if (i > 3) {
                        return observableThrowError(new Error('Max retries exceeded'));
                    }
                    return timer(Math.round( ( i + Math.random()) * 3000 ));
                }))
        ));
    }

    public getSignedUrlsForRecordingInInterBucket(key, urlValidityPeriod) {
        return this.http.post<any>(this.apiGatewayHelperService.getApiGatewayPrefixUrl() + 'signedUrlsForInterBucket', { key, urlValidityPeriod });
    }

    public setupPushXEventListener() {
        if (this.pushXListenerExist) {
            // As the PushXConnection already exists, don't need to create a new one
            return Promise.resolve();
        }
        return new Promise<void>((resolve, reject) => {
            const userData = this.authService.getUserData() || {};
            const extUserId = userData.extUserId;
            const context = 'TYPE=CUSTOM_EVENTS, CUSTOM_AUDIO_STREAM_PROCESSING';

            this.pushXService.grantByAccountId(extUserId).toPromise().then(() => {
                const pushXConnection = this.pushXService.getPushXConnection(extUserId);
                //Setup a connection using a USING ACCOUNT ID (for entitlements provisioning events)
                pushXConnection.on({ accountid: 'cup1', channel: `refid.${extUserId}` }, (sysevent) => {
                    const eventBody = (sysevent.events && sysevent.events.body) || {};
                    this.pushXEventSubject.next(eventBody);
                    this.pushXLogger(userData, context, eventBody, true).catch(e => { throw e; });
                });

                //Setup Handler to all status related to PUSH which will give both error and success
                pushXConnection.on({ channel: 'pushx_status' }, (subscriptionStatus) => {
                    try {
                        if (subscriptionStatus.category === 'PUSHX' && subscriptionStatus.type === 'CHANNEL_SUBSCRIPTION') {
                            if (subscriptionStatus.status === 'SUCCESS') {
                                this.pushXListenerExist = true;
                                resolve();
                            } else if (subscriptionStatus.status === 'ERROR') {
                                throw new Error(JSON.stringify(subscriptionStatus));
                            }
                        }
                    } catch (error) {
                        const listernerExist = this.pushXListenerExist;
                        this.pushXCleanup();
                        if (listernerExist) {
                            this.pushXEventSubject.next({ subscriptionError: true, error });
                            throw error;
                        } else {
                            reject(error);
                        }
                    }
                });
            }).catch(reject);
        });
    }

    public pushXCleanup() {
        this.pushXListenerExist = false;
        this.pushXService.cleanup();
    }

    private async pushXLogger(userData: any, context: string, message: string, success: boolean) {
        const logObj: any = {
            message, context,
            extUserId: userData.extUserId,
            role: userData.role,
            status: !success ? 'error' : 'success'
        };
        await this.customBackendLoggerService.logInfoToBackend(logObj).toPromise();
    }

    getSignedURL(data: {url: string, timestampQueryParamRequired: boolean, urlValidityPeriod: number}): Promise<any> {
        return this.http.post<any>(this.apiGatewayHelperService.getApiGatewayPrefixUrl() + 'submissionSignedUrl', data).toPromise();
    }

}